import { type User } from 'components/framework/models';
import Effort, { type Performance } from './Effort';
import { type RecordEventRaw } from 'components/framework/models/generated/types';

export default class UserPerformance implements Performance {
  committed = new Effort('committed');
  added = new Effort('added');
  removed = new Effort('removed');
  completed = new Effort('completed');
  incomplete = new Effort('incomplete');

  /**
   * Records that have been assigned to this user
   */
  recordsAssigned: Set<string> = new Set();

  static combine(userPerformances: UserPerformance[]) {
    const combined = {
      committed: new Effort('committed'),
      added: new Effort('added'),
      removed: new Effort('removed'),
      completed: new Effort('completed'),
      incomplete: new Effort('incomplete'),
    } as const;
    const fields = Object.keys(combined);

    return userPerformances.reduce<typeof combined>((acc, user) => {
      fields.forEach(field => {
        if (acc[field]) {
          acc[field].add(user[field]);
        } else {
          acc[field] = user[field].clone();
        }
      });

      return acc;
    }, combined);
  }

  get id() {
    return this.user.id;
  }

  constructor(public user: User) {}

  assignRecord(recordEvent: RecordEventRaw) {
    this.recordsAssigned.add(
      [recordEvent.eventSubjectType, recordEvent.eventSubjectId].join('-')
    );
  }

  recordAlreadySeen(recordEvent: RecordEventRaw) {
    return this.recordsAssigned.has(
      [recordEvent.eventSubjectType, recordEvent.eventSubjectId].join('-')
    );
  }

  clone(): UserPerformance {
    const clone = new UserPerformance(this.user);

    ['committed', 'added', 'removed', 'completed', 'incomplete'].forEach(
      field => {
        clone[field] = this[field].clone();
      }
    );

    return clone;
  }
}
